import React, { useEffect, useState } from "react";
import {
  PUBLIC_MARKETING_COOKIES_ACCEPTED_EVENT,
  readPublicMarketingCookieConsent,
} from "./publicMarketingConsent";

/** GTM container — only mount via this component on public marketing routes. */
const GTM_ID = "GTM-TZPK4NP6";
const SCRIPT_ID = "tkl-public-gtm-js";

type PublicGtmProps = {
  children: React.ReactNode;
};

/**
 * Loads GTM only after marketing cookie consent. Removes the script when this
 * component unmounts (e.g. leaving public routes).
 */
export function PublicGtm({ children }: PublicGtmProps) {
  const [consentGranted, setConsentGranted] = useState(() =>
    readPublicMarketingCookieConsent()
  );

  useEffect(() => {
    const sync = () => {
      if (readPublicMarketingCookieConsent()) {
        setConsentGranted(true);
      }
    };
    window.addEventListener(PUBLIC_MARKETING_COOKIES_ACCEPTED_EVENT, sync);
    return () => {
      window.removeEventListener(PUBLIC_MARKETING_COOKIES_ACCEPTED_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    if (!consentGranted) {
      return undefined;
    }

    const w = window as Window & { dataLayer?: object[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.remove();
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById(SCRIPT_ID)?.remove();
    };
  }, [consentGranted]);

  return (
    <>
      {consentGranted ? (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            title="Google Tag Manager"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
      ) : null}
      {children}
    </>
  );
}
